import type { AiDecision, AiRun } from '../api/DigiobcanApi';
import { __ } from '../utils';
import type { StatusChipTone } from '@presov.sk/presov-ui-react';

export type AiDecisionType = AiDecision['type'];

export const AI_DECISION_TYPE_LABEL: Record<AiDecisionType, string> = {
  categories: __('Kategorizácia'),
  explicit: __('Moderácia'),
  duplicates: __('Duplicity'),
};

/** „1 návrh" / „3 návrhy" / „7 návrhov" - slovenské skloňovanie. */
export const suggestionsLabel = (count: number) => {
  if (count === 1) return __('1 návrh');
  if (count >= 2 && count <= 4) return `${count} ${__('návrhy')}`;
  return `${count} ${__('návrhov')}`;
};

/**
 * Štítky moderácie po slovensky. Kľúče sú tie, ktoré vracia model
 * (`vlm-moderation-v1`, `HIDDEN_LABELS` v reports-analyzer), plus staré
 * z `few-shot-v1`. Neznámy štítok sa ukáže tak, ako prišiel - nový model
 * ho mohol pridať skôr, než sem prišiel preklad.
 */
const MODERATION_LABEL: Record<string, string> = {
  VULGARITY: __('Vulgarizmus'),
  INSULT: __('Urážka'),
  HARASSMENT: __('Obťažovanie'),
  HATE: __('Nenávisť'),
  THREAT: __('Vyhrážka'),
  VIOLENCE: __('Násilie'),
  SEXUAL: __('Sexuálny obsah'),
  EXTREMISM: __('Extrémizmus'),
  SPAM: __('Spam'),
  PII: __('Osobné údaje'),
  OTHER: __('Iné'),
  strong_language: __('Ostrý tón'),
  vulgarism: __('Vulgarizmus'),
  explicit_content: __('Nevhodný obsah'),
  sexual_content: __('Sexuálny obsah'),
  violence_threat_or_instruction: __('Vyhrážka alebo návod'),
};

export const moderationLabel = (label: string) =>
  MODERATION_LABEL[label] ?? label;

export const LABEL_SOURCE: Record<string, string> = {
  text: __('text'),
  image: __('fotka'),
};

/** Krátky výsledok behu do bunky tabuľky: čo AI uplatnila. */
export const aiDecisionResult = (decision: AiDecision): string | null => {
  if (decision.error) return __('Zlyhalo');
  if (decision.categorization) {
    return decision.categorization.applied?.name ?? null;
  }
  if (decision.moderation) {
    return decision.moderation.explicit ? __('Skryť') : __('Verejné');
  }
  if (decision.duplicates) {
    // Bez fotky s embeddingom detektor nemal z čoho hľadať - to nie je
    // „bez duplicít", len neposúdené.
    if (!decision.duplicates.mediaCount) return __('Bez fotky');
    const count = decision.duplicates.candidates.length;
    return count ? suggestionsLabel(count) : __('Bez duplicít');
  }
  return null;
};

/**
 * Istota do bunky. Pri moderácii je to najvyššie skóre štítku, ktorý model
 * našiel - samotný verdikt skóre nemá.
 */
export const aiDecisionScore = (decision: AiDecision): number | undefined => {
  if (decision.categorization) return decision.categorization.confidence;
  const scores = (decision.moderation?.labels ?? [])
    .map((label) => label.score)
    .filter((score): score is number => typeof score === 'number');
  return scores.length ? Math.max(...scores) : undefined;
};

export const percent = (value: number | undefined) =>
  value === undefined ? null : `${Math.round(value * 100)} %`;

export const DRY_RUN_LABEL = __('Skúšobný');

/**
 * Stav nedobehnutého behu (`GET /admin/ai-runs`). „Čaká" je úloha v rade
 * analyzera - pri skúšobnom behu idú po jednom, takže väčšina dávky čaká.
 */
export const AI_RUN_STATUS: Record<
  AiRun['status'],
  { label: string; tone: StatusChipTone }
> = {
  running: { label: __('Beží'), tone: 'info' },
  queued: { label: __('Čaká v rade'), tone: 'neutral' },
};

/**
 * Zhoda s úradníkom ako štítok. `undefined` je „nedá sa povedať" (beh
 * zlyhal alebo AI nič neuplatnila), nie nezhoda.
 *
 * Pri skúšobnom behu úradník nič neprepísal - nič sa neuplatnilo - takže
 * nezhoda je len „Nezhoda": model by dnes povedal niečo iné, než ako podnet
 * vyzerá.
 */
export const humanAgreement = (
  decision: Pick<AiDecision, 'humanAgrees' | 'dryRun'>,
): { label: string; tone: StatusChipTone } | null =>
  decision.humanAgrees === undefined
    ? null
    : decision.humanAgrees
      ? { label: __('Zhoda'), tone: 'success' }
      : {
          label: decision.dryRun ? __('Nezhoda') : __('Prepísané'),
          tone: 'warning',
        };

export const latency = (ms: number | undefined) =>
  ms === undefined
    ? null
    : ms < 1000
      ? `${ms} ms`
      : `${(ms / 1000).toLocaleString('sk-SK', { maximumFractionDigits: 1 })} s`;
